import { useEffect, useState } from "react";
import { Banner, Button, ConfirmDialog, Spinner, StatusBadge } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  approveDeviceRequest,
  blockUserDevice,
  disableUser,
  enableUser,
  endSession,
  errorDetail,
  forceLogoutUser,
  getUserSecurity,
  rejectDeviceRequest,
  resetUserDevice,
  unlockUser,
} from "@/lib/api";
import type { AuthUser, SessionInfo, UserSecurity } from "@/lib/types";
import { formatDateTime, parseApiDate } from "@/lib/format";
import { getStoredUser } from "@/lib/auth";
import { Ban, LogOut, Lock, ShieldCheck, Smartphone, UserX } from "lucide-react";
import { useLanguage } from "@/i18n";

/**
 * Phone & session control for one account: the registered phone (surveyors),
 * sign-in sessions, the wrong-password lock, and properties a phone uploaded
 * after the office had signed it out.
 *
 * Every rule lives in the backend (services/sessions.py); this only shows the
 * state and offers the office actions: approve or reject a waiting phone, reset
 * or block the registered phone, end one or every session, unlock, and disable
 * the account.
 */
export default function AccountSecurityDialog({
  user,
  onOpenChange,
  onChanged,
}: {
  user: AuthUser | null;
  onOpenChange: (open: boolean) => void;
  /** Called after an action, with a notice for the page, so the list refreshes. */
  onChanged: (notice: string) => void;
}) {
  const { t, lang } = useLanguage();
  const s = t.accountSecurity;
  const [data, setData] = useState<UserSecurity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<
    "logout" | "reset" | "block" | "disable" | SessionInfo | null
  >(null);
  const [allowFinalSync, setAllowFinalSync] = useState(true);

  useEffect(() => {
    setData(null);
    setError(null);
    setConfirm(null);
    if (!user) return;
    let cancelled = false;
    getUserSecurity(user.id)
      .then((d) => !cancelled && setData(d))
      .catch((err) => !cancelled && setError(errorDetail(err)));
    return () => {
      cancelled = true;
    };
  }, [user]);

  const when = (iso: string | null | undefined) =>
    iso ? formatDateTime(iso, lang, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  async function run(action: () => Promise<UserSecurity>, notice: string) {
    setBusy(true);
    setError(null);
    try {
      setData(await action());
      setConfirm(null);
      onChanged(notice);
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(false);
    }
  }

  const name = user?.full_name ? `${user.full_name} (${user.username})` : user?.username ?? "";
  const activeSessions = data?.sessions.filter((x) => x.active) ?? [];
  const isSelf = user !== null && getStoredUser()?.id === user.id;
  const ending = typeof confirm === "object" ? confirm : null;

  const confirmText = ending
    ? { title: s.endSessionTitle, body: s.endSessionBody(name), label: s.endSessionConfirm }
    : confirm === "reset"
      ? { title: s.resetDeviceTitle, body: s.resetDeviceBody(name), label: s.resetDeviceConfirm }
      : confirm === "block"
        ? { title: s.blockDeviceTitle, body: s.blockDeviceBody(name), label: s.blockDeviceConfirm }
        : confirm === "disable"
          ? { title: s.disableTitle, body: s.disableBody(name), label: s.disableConfirm }
          : { title: s.forceLogoutTitle, body: s.forceLogoutBody(name), label: s.forceLogoutConfirm };

  // Ends one session, then re-reads the account (that endpoint returns nothing).
  const endOne = (userId: number, sessionId: number, allow: boolean) => async () => {
    await endSession(sessionId, allow);
    return getUserSecurity(userId);
  };

  return (
    <>
      <Dialog open={user !== null} onOpenChange={(open) => !busy && onOpenChange(open)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span>{s.title}</span>
            </DialogTitle>
            <DialogDescription>
              {s.description} <span className="font-semibold text-ink">{name}</span>.
            </DialogDescription>
          </DialogHeader>

          {error && <Banner tone="danger">{error}</Banner>}

          {data === null ? (
            !error && (
              <div className="flex items-center justify-center gap-2 py-12 text-xs text-ink-muted">
                <Spinner className="h-4 w-4" /> {s.loading}
              </div>
            )
          ) : (
            <div className="thin-scroll max-h-[65vh] space-y-4 overflow-y-auto pr-1">
              {/* Registered phone */}
              <Section
                icon={<Smartphone className="h-3.5 w-3.5" />}
                title={s.deviceHeading}
                action={
                  data.device_binding && data.device ? (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="secondary" disabled={busy} onClick={() => { setAllowFinalSync(true); setConfirm("reset"); }}>
                        {s.resetDevice}
                      </Button>
                      <Button size="sm" variant="danger" disabled={busy} onClick={() => { setAllowFinalSync(false); setConfirm("block"); }}>
                        <Ban className="mr-1 h-3.5 w-3.5" />
                        {s.blockDevice}
                      </Button>
                    </div>
                  ) : null
                }
              >
                {!data.device_binding ? (
                  <p className="text-xs text-ink-muted">{s.deviceNotBound}</p>
                ) : data.device ? (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-3">
                    <Item label={s.deviceModel} value={data.device.device_model ?? "—"} />
                    <Item label={s.deviceAndroid} value={data.device.os_version ?? "—"} />
                    <Item label={s.deviceApp} value={data.device.app_version ?? "—"} />
                    <Item label={s.deviceRegistered} value={when(data.device.registered_at)} />
                    <Item label={s.deviceLastLogin} value={when(data.device.last_login_at)} />
                    <Item label={s.deviceId} value={<code className="font-mono text-2xs">{data.device.device_id.slice(0, 8)}…</code>} title={data.device.device_id} />
                  </dl>
                ) : (
                  <p className="text-xs text-ink-muted">
                    {data.approval_required ? s.deviceNoneApproval : s.deviceNone}
                  </p>
                )}
                {data.device_requests.length > 0 && (
                  <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2.5">
                    <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-amber-900">{s.pendingHeading}</p>
                    <ul className="space-y-2">
                      {data.device_requests.map((r) => (
                        <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                          <span className="text-ink">
                            {r.device_model ?? r.device_id.slice(0, 8)}
                            {r.app_version && <span className="ml-1 text-2xs text-ink-muted">v{r.app_version}</span>}
                            <span className="ml-1.5 text-2xs text-ink-muted">{s.pendingLastTry(when(r.last_attempt_at))}</span>
                            {r.replaces_model && (
                              <span className="block text-2xs text-amber-800">{s.pendingReplaces(r.replaces_model)}</span>
                            )}
                          </span>
                          <span className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={busy}
                              onClick={() => user && void run(() => approveDeviceRequest(r.id), s.noticeApproved(user.username))}
                            >
                              {s.approve}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => user && void run(() => rejectDeviceRequest(r.id, false), s.noticeRejected(user.username))}
                            >
                              {s.reject}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={busy}
                              onClick={() => user && void run(() => rejectDeviceRequest(r.id, true), s.noticeRejected(user.username))}
                            >
                              {s.rejectBlock}
                            </Button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.previous_devices.length > 0 && (
                  <div className="mt-3 border-t border-line pt-2">
                    <p className="mb-1 text-2xs font-semibold uppercase tracking-wider text-ink-muted">{s.previousDevices}</p>
                    <ul className="space-y-0.5 text-2xs text-ink-secondary">
                      {data.previous_devices.map((d) => (
                        <li key={d.id}>
                          {d.device_model ?? d.device_id.slice(0, 8)} — {s.releasedBy(when(d.released_at), d.released_by ?? "")}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>

              {/* Sessions */}
              <Section
                icon={<LogOut className="h-3.5 w-3.5" />}
                title={s.sessionHeading}
                action={
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy || activeSessions.length === 0}
                    onClick={() => { setAllowFinalSync(true); setConfirm("logout"); }}
                  >
                    {s.forceLogout}
                  </Button>
                }
              >
                <p className="mb-2 text-2xs text-ink-muted">{s.sessionLimit(data.max_sessions)}</p>
                {activeSessions.length === 0 && data.sessions.length > 0 && (
                  <p className="mb-2 text-xs text-ink-muted">{s.noActiveSession}</p>
                )}
                {data.sessions.length === 0 ? (
                  <p className="text-xs text-ink-muted">{s.sessionNone}</p>
                ) : (
                  <div className="thin-scroll overflow-x-auto rounded border border-line">
                    <table className="w-full min-w-[560px] text-left text-xs">
                      <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                        <tr>
                          <th className="px-2.5 py-1.5 font-semibold" />
                          <th className="px-2.5 py-1.5 font-semibold">{s.sessionDevice}</th>
                          <th className="px-2.5 py-1.5 font-semibold">{s.sessionStarted}</th>
                          <th className="px-2.5 py-1.5 font-semibold">{s.sessionLastActivity}</th>
                          <th className="px-2.5 py-1.5 font-semibold">{s.sessionIp}</th>
                          <th className="px-2.5 py-1.5 font-semibold" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {data.sessions.map((x) => (
                          <tr key={x.id}>
                            <td className="whitespace-nowrap px-2.5 py-1.5 align-top">
                              <SessionState session={x} />
                            </td>
                            <td className="px-2.5 py-1.5 align-top text-ink-secondary">
                              {x.client === "portal" ? s.sessionBrowser : x.device_model ?? x.device_id?.slice(0, 8) ?? "—"}
                              {x.app_version && <span className="ml-1 text-2xs text-ink-muted">v{x.app_version}</span>}
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-1.5 align-top tabular-nums text-ink-secondary">{when(x.started_at)}</td>
                            <td className="whitespace-nowrap px-2.5 py-1.5 align-top tabular-nums text-ink-secondary">{when(x.last_seen_at)}</td>
                            <td className="px-2.5 py-1.5 align-top font-mono text-2xs text-ink-muted">{x.ip ?? "—"}</td>
                            <td className="px-2.5 py-1 align-top text-right">
                              {x.active && !isSelf && (
                                <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setAllowFinalSync(true); setConfirm(x); }}>
                                  {s.endSession}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* Lockout */}
              <Section
                icon={<Lock className="h-3.5 w-3.5" />}
                title={s.lockHeading}
                action={
                  data.locked_minutes > 0 ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => user && void run(() => unlockUser(user.id), s.noticeUnlocked(user.username))}
                    >
                      {s.unlock}
                    </Button>
                  ) : null
                }
              >
                <p className="text-xs text-ink-secondary">
                  {data.locked_minutes > 0
                    ? s.lockedFor(data.locked_minutes)
                    : data.failed_attempts > 0
                      ? s.failedSoFar(data.failed_attempts)
                      : s.notLocked}
                </p>
              </Section>

              {/* Account access */}
              <Section
                icon={<UserX className="h-3.5 w-3.5" />}
                title={s.accountHeading}
                action={
                  isSelf ? null : data.disabled ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => user && void run(() => enableUser(user.id), s.noticeEnabled(user.username))}
                    >
                      {s.enable}
                    </Button>
                  ) : (
                    <Button size="sm" variant="danger" disabled={busy} onClick={() => { setAllowFinalSync(true); setConfirm("disable"); }}>
                      {s.disable}
                    </Button>
                  )
                }
              >
                <p className="text-xs text-ink-secondary">
                  {data.disabled ? s.accountDisabledText(data.disabled_by ?? "", when(data.disabled_at)) : s.accountActive}
                </p>
                {data.disabled && data.disabled_reason && (
                  <p className="mt-1 text-2xs text-ink-muted">{data.disabled_reason}</p>
                )}
              </Section>

              {/* Late uploads */}
              {data.device_binding || data.late_uploads_total > 0 ? (
                <Section title={s.lateHeading}>
                  <p className="mb-2 text-2xs text-ink-muted">{s.lateIntro}</p>
                  {data.late_uploads.length === 0 ? (
                    <p className="text-xs text-ink-muted">{s.lateNone}</p>
                  ) : (
                    <>
                      <div className="thin-scroll max-h-56 overflow-auto rounded border border-line">
                        <table className="w-full text-left text-xs">
                          <thead className="sticky top-0 bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                            <tr>
                              <th className="px-2.5 py-1.5 font-semibold">{s.lateProperty}</th>
                              <th className="px-2.5 py-1.5 font-semibold">{s.lateBlock}</th>
                              <th className="px-2.5 py-1.5 font-semibold">{s.lateAction}</th>
                              <th className="px-2.5 py-1.5 font-semibold">{s.lateWhen}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-line">
                            {data.late_uploads.map((u, i) => (
                              <tr key={`${u.feature_id}-${i}`}>
                                <td className="px-2.5 py-1.5 text-ink">
                                  {u.property_no ?? `#${u.feature_id}`}
                                  {!u.exists && <span className="ml-1 text-2xs text-ink-muted">({s.lateDeleted})</span>}
                                </td>
                                <td className="px-2.5 py-1.5 text-ink-secondary">{u.block_name ?? "—"}</td>
                                <td className="px-2.5 py-1.5 text-ink-secondary">{s.lateActions[u.action] ?? u.action}</td>
                                <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums text-ink-secondary">{when(u.at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {data.late_uploads_total > data.late_uploads.length && (
                        <p className="mt-1 text-2xs text-ink-muted">
                          {s.lateMore(data.late_uploads.length, data.late_uploads_total)}
                        </p>
                      )}
                    </>
                  )}
                </Section>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirm !== null}
        title={confirmText.title}
        body={confirmText.body}
        confirmLabel={confirmText.label}
        cancelLabel={t.common.cancel}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!user) return;
          if (ending) {
            void run(endOne(user.id, ending.id, allowFinalSync), s.noticeSessionEnded);
          } else if (confirm === "reset") {
            void run(() => resetUserDevice(user.id, allowFinalSync), s.noticeDeviceReset(user.username));
          } else if (confirm === "block") {
            void run(() => blockUserDevice(user.id, allowFinalSync), s.noticeDeviceBlocked(user.username));
          } else if (confirm === "disable") {
            void run(() => disableUser(user.id, allowFinalSync), s.noticeDisabled(user.username));
          } else {
            void run(() => forceLogoutUser(user.id, allowFinalSync), s.noticeLoggedOut(user.username));
          }
        }}
      >
        {(ending ? ending.client === "app" : data?.device_binding) && (
          <label className="flex cursor-pointer items-start gap-2 rounded bg-surface-sunken p-2.5">
            <input
              type="checkbox"
              className="mt-0.5 accent-accent-solid"
              checked={allowFinalSync}
              onChange={(e) => setAllowFinalSync(e.target.checked)}
              disabled={busy}
            />
            <span>
              <span className="block text-xs font-medium text-ink">{s.allowFinalSync}</span>
              <span className="block text-2xs text-ink-muted">{s.allowFinalSyncHint}</span>
            </span>
          </label>
        )}
      </ConfirmDialog>
    </>
  );
}

function Section({
  icon,
  title,
  action,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-line p-3">
      <header className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-ink">
          {icon && <span className="text-ink-muted">{icon}</span>}
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

function Item({ label, value, title }: { label: string; value: React.ReactNode; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <dt className="text-2xs text-ink-muted">{label}</dt>
      <dd className="truncate text-ink">{value}</dd>
    </div>
  );
}

function SessionState({ session }: { session: SessionInfo }) {
  const { t } = useLanguage();
  const s = t.accountSecurity;
  if (session.active) return <StatusBadge tone="good" label={s.sessionActive} />;
  if (!session.ended_at) return <StatusBadge tone="neutral" label={s.sessionExpired} />;
  const reason = session.end_reason ? s.endReason[session.end_reason] ?? session.end_reason : "";
  const finalNote =
    session.end_reason &&
    session.client === "app" &&
    session.end_reason !== "logout" &&
    session.end_reason !== "replaced"
      ? session.final_sync_closed_at
        ? s.finalUploadDone
        : session.final_sync_allowed && parseApiDate(session.expires_at) > new Date()
          ? s.finalUploadPending
          : null
      : null;
  return (
    <div>
      <StatusBadge tone="neutral" label={s.sessionEnded} />
      {reason && (
        <p className="ml-4 text-2xs text-ink-muted">
          {reason}
          {session.ended_by ? ` · ${session.ended_by}` : ""}
        </p>
      )}
      {finalNote && <p className="ml-4 text-2xs text-ink-muted">{finalNote}</p>}
    </div>
  );
}
